import { Fragment, useRef } from 'react';
import { Clock } from './Clock';
import { ComicBoard } from './ComicBoard';
import { StoryText } from './StoryText';
import { beamFont } from '../../shared/fonts';
import { formatClock } from '../../shared/format';
import {
  CLIP_HEIGHT,
  CLIP_WIDTH,
  clipChips,
  clipPlayhead,
  clipSection,
  clipStats,
  type ClipPlan,
} from '../../shared/clip';
import { stateAt, type PlaybackRecording } from '../../shared/playback';
import { useFollowScroll } from '../lib/followScroll';

/**
 * The vertical clip frame (2026-09-09): one round as a finished 9:16 film,
 * for Reels / TikTok / Shorts. No camera, no editing.
 *
 * Not the projection stage. A beamer is a wide frame read from ten metres
 * away with a margin for annotations; a phone is a tall frame read from
 * thirty centimetres with a thumb over the bottom eighth. So the clocks sit
 * side by side under the title, the story runs down the middle in the type
 * it was written in, and what the projection puts in its left margin — a
 * joker, a penalty, a clock hitting zero — arrives here as a chip that holds
 * for a few seconds.
 *
 * Two hard rules, both because this component is rendered **frame by frame**
 * by a headless browser (scripts/render-clip.mts):
 *
 *  · It is a pure function of `outMs`. No `Date.now()`, no timers, no random.
 *    Frame 431 must look the same whether it is rendered first or last.
 *  · CSS animations are seeked, not played: every animation inside `.clip`
 *    is paused and given a negative delay taken from the playhead, so the
 *    caret blinks and the VS badge pulses in *clip* time. Left to run on
 *    wall time they would flicker at random through the render.
 *
 * The frame is authored at exactly 1080 × 1920 and scaled by the caller, so
 * what the browser preview shows is what the render writes out.
 */
export function ClipStage(props: {
  rec: PlaybackRecording;
  plan: ClipPlan;
  /** Position in the finished clip, ms. */
  outMs: number;
  /** Comic sheet for a Bildrunde — blob URL or data URL. */
  comicSrc?: string | null;
  /** Handle shown on the end card; '' hides it. */
  cta?: string;
}) {
  const { rec, plan, outMs, comicSrc, cta = 'arena.fluegelmann.de' } = props;
  const playhead = clipPlayhead(plan, outMs);
  const section = clipSection(plan, outMs);
  const state = stateAt(rec, playhead);
  const round = state.round;
  const comic = round.comicId === null ? undefined : state.comics.find((c) => c.id === round.comicId);
  const roundLabel = round.kind === 'comic' ? `Bildrunde ${round.number}` : `Runde ${round.number}`;

  const frameStyle = {
    width: CLIP_WIDTH,
    height: CLIP_HEIGHT,
    '--story-font': beamFont(state.display.fontId).stack,
    '--story-scale': state.display.fontScale,
    // Seek, don't play: every animation in the frame is paused at this offset.
    '--clip-anim': `-${Math.round(outMs)}ms`,
  } as React.CSSProperties;

  return (
    <div className="clip" style={frameStyle} data-testid="clip-frame" data-clip-section={section}>
      {section === 'versus' && <VersusCard rec={rec} roundLabel={roundLabel} />}
      {section === 'task' && <TaskCard rec={rec} state={state} comicSrc={comicSrc} roundLabel={roundLabel} />}
      {section === 'round' && (
        <RoundFrame rec={rec} state={state} playhead={playhead} comic={comic} comicSrc={comicSrc} roundLabel={roundLabel} />
      )}
      {section === 'outro' && <EndCard rec={rec} roundLabel={roundLabel} cta={cta} />}
    </div>
  );
}

/* --------------------------------------------------------------- versus - */

function VersusCard({ rec, roundLabel }: { rec: PlaybackRecording; roundLabel: string }) {
  return (
    <>
      <ClipHead roundLabel={roundLabel} prompt={null} />
      <main className="clip__body clip__body--centre">
        <div className="versus clip__versus" data-testid="clip-versus">
          {rec.authors.map((seat, i) => (
            <Fragment key={seat.index}>
              {i > 0 && <span className="versus__vs">VS</span>}
              <span
                className="versus__name"
                style={{ '--seat-color': seat.color } as React.CSSProperties}
                data-text={seat.name}
              >
                {seat.name}
              </span>
            </Fragment>
          ))}
        </div>
        {rec.prompt && <div className="clip__versus-prompt">„{rec.prompt}“</div>}
      </main>
      <ClipFoot />
    </>
  );
}

/* ----------------------------------------------------------------- task - */

function TaskCard({
  rec,
  state,
  comicSrc,
  roundLabel,
}: {
  rec: PlaybackRecording;
  state: ReturnType<typeof stateAt>;
  comicSrc?: string | null;
  roundLabel: string;
}) {
  const round = state.round;
  const comic = round.comicId === null ? undefined : state.comics.find((c) => c.id === round.comicId);
  return (
    <>
      <ClipHead roundLabel={roundLabel} prompt={null} />
      <main className="clip__body clip__body--centre">
        {round.kind === 'comic' && comic && comicSrc ? (
          <ComicBoard
            comic={comic}
            src={comicSrc}
            round={round}
            authors={state.authors}
            captions={false}
            layout="below"
            className="comicboard--clip"
          />
        ) : null}
        <div className="clip__task" data-testid="clip-task">
          <div className="clip__kicker">Die Aufgabe</div>
          <div className="clip__taskline">{round.prompt || roundLabel}</div>
        </div>
        <div className="clip__seats">
          {rec.authors.map((seat) => (
            <span key={seat.index} className="clip__seat" style={{ borderColor: seat.color }}>
              {seat.name}
              {seat.index === round.activeAuthor ? ' beginnt' : ''}
            </span>
          ))}
        </div>
      </main>
      <ClipFoot />
    </>
  );
}

/* ---------------------------------------------------------------- round - */

function RoundFrame({
  rec,
  state,
  playhead,
  comic,
  comicSrc,
  roundLabel,
}: {
  rec: PlaybackRecording;
  state: ReturnType<typeof stateAt>;
  playhead: number;
  comic: { id: string } | undefined;
  comicSrc?: string | null;
  roundLabel: string;
}) {
  const round = state.round;
  const storyRef = useRef<HTMLDivElement>(null);
  useFollowScroll(storyRef, round.text.length);
  const chips = clipChips(rec, playhead);

  return (
    <>
      <ClipHead roundLabel={roundLabel} prompt={round.prompt} />

      <div className="clip__clocks">
        {rec.authors.map((seat) => (
          <Clock
            key={seat.index}
            seat={seat}
            seatIndex={seat.index}
            round={round}
            remainingMs={round.remainingMs[seat.index] ?? 0}
            size="L"
            className="clip__clock"
          />
        ))}
      </div>

      <div className="clip__chips" data-testid="clip-chips">
        {chips.map((chip, i) => (
          <span key={`${chip.atMs}-${i}`} className={`clip__chip clip__chip--${chip.kind}`}>
            {chip.kind === 'joker' ? `Joker · ${chip.label}` : chip.label}
          </span>
        ))}
      </div>

      {round.kind === 'comic' && comic && comicSrc ? (
        <main className="clip__body clip__body--comic">
          <ComicBoard
            comic={state.comics.find((c) => c.id === comic.id)!}
            src={comicSrc}
            round={round}
            authors={state.authors}
            showCaret={round.phase === 'running'}
            layout="below"
            className="comicboard--clip"
          />
        </main>
      ) : (
        <main ref={storyRef} className="clip__body clip__body--story">
          <StoryText round={round} authors={state.authors} showCaret={round.phase === 'running'} className="story--clip" />
        </main>
      )}

      {round.phase === 'paused' && <div className="clip__banner">Pause</div>}
      <ClipFoot />
    </>
  );
}

/* ------------------------------------------------------------------ end - */

function EndCard({ rec, roundLabel, cta }: { rec: PlaybackRecording; roundLabel: string; cta: string }) {
  const stats = clipStats(rec);
  const reason =
    rec.endReason === 'time'
      ? 'Beide Uhren auf 0:00'
      : rec.endReason === 'completed'
        ? rec.kind === 'comic'
          ? 'Alle sechs Panels beschriftet'
          : 'Gemeinsam für fertig erklärt'
        : 'Von der Moderation beendet';

  return (
    <>
      <ClipHead roundLabel={roundLabel} prompt={null} />
      <main className="clip__body clip__body--end" data-testid="clip-end">
        <div className="clip__ende">Ende</div>
        <div className="clip__reason">{reason}</div>

        <div className="clip__numbers">
          <Number value={String(stats.words)} label="Wörter" />
          <Number value={String(stats.chars)} label="Zeichen" />
          <Number value={String(stats.charsPerMinute)} label="Zeichen / Min" />
          <Number value={formatClock(rec.roundMs)} label="Spielzeit" />
        </div>

        <div className="clip__shares">
          {stats.authors.map((a) => (
            <div key={a.seat.index} className="clip__share">
              <div className="clip__share-head">
                <span style={{ color: a.seat.color }}>{a.seat.name}</span>
                <span>{a.words} Wörter</span>
              </div>
              <div className="clip__share-bar">
                <span style={{ width: `${Math.round(a.share * 100)}%`, background: a.seat.color }} />
              </div>
            </div>
          ))}
        </div>

        {stats.blocked > 0 && (
          <div className="clip__blocked">
            {stats.blocked}× Löschen versucht — <strong>gelöscht wird nicht.</strong>
          </div>
        )}
      </main>
      <ClipFoot cta={cta} />
    </>
  );
}

function Number({ value, label }: { value: string; label: string }) {
  return (
    <div className="clip__number">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

/* ------------------------------------------------------------- furniture - */

function ClipHead({ roundLabel, prompt }: { roundLabel: string; prompt: string | null }) {
  return (
    <header className="clip__head">
      <img className="clip__logo" src="/img/osiander-logo.png" alt="Osiander" />
      <div className="clip__brand">Schreibarena</div>
      <div className="clip__round">{roundLabel}</div>
      {prompt && <div className="clip__prompt">{prompt}</div>}
    </header>
  );
}

function ClipFoot({ cta }: { cta?: string }) {
  return (
    <footer className="clip__foot">
      <span>Zwei Autor:innen · eine Geschichte · eine Schachuhr</span>
      {cta ? <strong>{cta}</strong> : <span>Gelöscht wird nicht.</span>}
    </footer>
  );
}
